import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

const { FiUser, FiMail, FiCamera, FiX, FiSave, FiMoon, FiSun, FiLock } = FiIcons;

const ProfileSettings = () => {
  const { user, updateProfile } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    avatar: user?.avatar || '',
  });
  
  const [activeTab, setActiveTab] = useState('profile');
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    updateProfile(formData);
    // Show success notification here
  };
  
  return (
    <div className="min-h-screen">
      <div className="space-y-6 p-8 ml-72">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg w-full overflow-hidden">
          {/* Header */}
          <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-800 dark:text-white">Profile Settings</h2>
          </div>
          
          {/* Tabs */}
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            <button
              className={`px-6 py-3 font-medium text-sm transition-colors relative ${
                activeTab === 'profile'
                  ? 'text-primary-600 dark:text-primary-400'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
              onClick={() => setActiveTab('profile')}
            >
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiUser} />
                <span>Personal Info</span>
              </div>
              {activeTab === 'profile' && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 dark:bg-primary-400"
                ></motion.div>
              )}
            </button>
            
            <button
              className={`px-6 py-3 font-medium text-sm transition-colors relative ${
                activeTab === 'appearance'
                  ? 'text-primary-600 dark:text-primary-400'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
              onClick={() => setActiveTab('appearance')}
            >
              <div className="flex items-center space-x-2">
                <SafeIcon icon={darkMode ? FiMoon : FiSun} />
                <span>Appearance</span>
              </div>
              {activeTab === 'appearance' && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 dark:bg-primary-400"
                ></motion.div>
              )}
            </button>
            
            <button
              className={`px-6 py-3 font-medium text-sm transition-colors relative ${
                activeTab === 'security'
                  ? 'text-primary-600 dark:text-primary-400'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
              onClick={() => setActiveTab('security')}
            >
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiLock} />
                <span>Security</span>
              </div>
              {activeTab === 'security' && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 dark:bg-primary-400"
                ></motion.div>
              )}
            </button>
          </div>
          
          {/* Content */}
          <div className="p-6 overflow-y-auto">
            {activeTab === 'profile' && (
              <form onSubmit={handleSubmit}>
                <div className="flex flex-col md:flex-row md:space-x-6">
                  {/* Avatar */}
                  <div className="mb-6 flex flex-col items-center">
                    <div className="relative">
                      <img 
                        src={formData.avatar || 'https://via.placeholder.com/150'} 
                        alt="Profile" 
                        className="w-32 h-32 rounded-xl object-cover border-4 border-white dark:border-gray-700 shadow-lg"
                      />
                      <button 
                        type="button" 
                        className="absolute bottom-2 right-2 bg-primary-600 text-white p-2 rounded-full hover:bg-primary-700 transition-colors"
                      >
                        <SafeIcon icon={FiCamera} className="text-sm" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                      Click to upload a new profile picture<br/>
                      Recommended: 400x400px
                    </p>
                  </div>
                  
                  {/* Form Fields */}
                  <div className="flex-1 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Full Name
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                          <SafeIcon icon={FiUser} className="text-gray-400" />
                        </div>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full pl-10 p-2.5"
                          placeholder="Your name"
                          required
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Email Address
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                          <SafeIcon icon={FiMail} className="text-gray-400" />
                        </div>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full pl-10 p-2.5"
                          placeholder="your.email@example.com"
                          readOnly
                        />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Email cannot be changed directly. Please contact support for email changes.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 flex justify-end">
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 flex items-center"
                  >
                    <SafeIcon icon={FiSave} className="mr-2" />
                    Save Changes
                  </button>
                </div>
              </form>
            )}
            
            {activeTab === 'appearance' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Theme Preferences</h3>
                  
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-800 dark:text-white">Dark Mode</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Switch between light and dark themes
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={toggleDarkMode}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                          darkMode ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`${
                            darkMode ? 'translate-x-6' : 'translate-x-1'
                          } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Interface Density</h3>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <button className="border-2 border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:border-primary-500 dark:hover:border-primary-400 transition-colors">
                      <div className="h-20 bg-gray-100 dark:bg-gray-700 rounded mb-2"></div>
                      <p className="text-sm font-medium text-gray-800 dark:text-white">Compact</p>
                    </button>
                    
                    <button className="border-2 border-primary-500 dark:border-primary-400 rounded-lg p-4 bg-primary-50 dark:bg-primary-900/10">
                      <div className="h-20 bg-gray-100 dark:bg-gray-700 rounded mb-2"></div>
                      <p className="text-sm font-medium text-primary-600 dark:text-primary-400">Default</p>
                    </button>
                    
                    <button className="border-2 border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:border-primary-500 dark:hover:border-primary-400 transition-colors">
                      <div className="h-20 bg-gray-100 dark:bg-gray-700 rounded mb-2"></div>
                      <p className="text-sm font-medium text-gray-800 dark:text-white">Comfortable</p>
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'security' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Password</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Current Password
                      </label>
                      <input
                        type="password"
                        className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5"
                        placeholder="••••••••"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        New Password
                      </label>
                      <input
                        type="password"
                        className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5"
                        placeholder="••••••••"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5"
                        placeholder="••••••••"
                      />
                    </div>
                    
                    <div className="pt-2">
                      <button
                        type="button"
                        className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
                      >
                        Update Password
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Sessions</h3>
                  
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium text-gray-800 dark:text-white">Current Session</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Chrome on Windows • {new Date().toLocaleDateString()}
                        </p>
                      </div>
                      <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 text-xs rounded-full">
                        Active
                      </span>
                    </div>
                  </div>
                  
                  <button
                    type="button"
                    className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30"
                  >
                    Log out from all other devices
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;